import { useMemo, useState } from 'react';
import { Loader2, Plus, Copy, Check, Ban, Search, Pencil } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetPromoCodesQuery, useCreatePromoCodeMutation, useDisablePromoCodeMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import EditPromoModal from './EditPromoModal';
import { usageLabel } from './usage';
import type { PromoCode, PromoStatus, CreatePromoCodeRequest } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

const STATUS_BADGE: Record<PromoStatus, string> = {
    active: 'bg-green-50 text-green-700 border-green-100',
    used: 'bg-gray-100 text-gray-600 border-gray-200',
    disabled: 'bg-red-50 text-red-700 border-red-100',
};

type Filter = 'all' | PromoStatus;

export default function PromoCodes() {
    const currentUser = useAppSelector(selectCurrentUser);
    const [filter, setFilter] = useState<Filter>('all');
    const { data: promos = [], isLoading } = useGetPromoCodesQuery(filter === 'all' ? undefined : { status: filter });
    const [disablePromo] = useDisablePromoCodeMutation();
    const [search, setSearch] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [copiedId, setCopiedId] = useState('');
    const [rowError, setRowError] = useState('');
    const [editing, setEditing] = useState<PromoCode | null>(null);

    const filtered = useMemo(() => {
        if (!search.trim()) return promos;
        const q = search.toLowerCase();
        return promos.filter((p) => p.code.toLowerCase().includes(q) || (p.note ?? '').toLowerCase().includes(q));
    }, [promos, search]);

    const handleCopy = (code: string, id: string) => {
        navigator.clipboard?.writeText(code).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(''), 1500);
        });
    };

    const handleDisable = async (id: string) => {
        setRowError('');
        try {
            await disablePromo(id).unwrap();
        } catch (err) {
            setRowError(parseErrorMessage(err, 'Failed to disable code.'));
        }
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Promo Codes</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Single-use, capped or unlimited codes for a percentage discount or a free investigation.
                            </p>
                        </div>
                        <Button
                            onClick={() => setModalOpen(true)}
                            className="self-start bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Create code
                        </Button>
                    </div>

                    {/* Filters + search */}
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search code or note"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto">
                            {(['all', 'active', 'used', 'disabled'] as Filter[]).map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                                        filter === f
                                            ? 'bg-gray-900 text-white'
                                            : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>

                    {rowError && (
                        <div className="rounded-xl border border-red-100 bg-red-50 text-red-700 text-sm px-4 py-3">
                            {rowError}
                        </div>
                    )}

                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">No promo codes found.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[860px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Code</th>
                                            <th className="px-4 py-3">Reward</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3">Usage</th>
                                            <th className="px-4 py-3">Note</th>
                                            <th className="px-4 py-3">Used by</th>
                                            <th className="px-4 py-3">Expires</th>
                                            <th className="px-4 py-3 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((promo) => (
                                            <PromoRow
                                                key={promo.id}
                                                promo={promo}
                                                copied={copiedId === promo.id}
                                                onCopy={() => handleCopy(promo.code, promo.id)}
                                                onDisable={() => handleDisable(promo.id)}
                                                onEdit={() => setEditing(promo)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CreatePromoModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
            <EditPromoModal promo={editing} onClose={() => setEditing(null)} />
        </main>
    );
}

function PromoRow({
    promo,
    copied,
    onCopy,
    onDisable,
    onEdit,
}: {
    promo: PromoCode;
    copied: boolean;
    onCopy: () => void;
    onDisable: () => void;
    onEdit: () => void;
}) {
    const reward = promo.rewardType === 'free' ? 'Free' : `${promo.percentOff ?? 0}% off`;
    const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
            <td className="px-4 py-4">
                <button
                    type="button"
                    onClick={onCopy}
                    className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-[#FF6B35]"
                    title="Copy code"
                >
                    {promo.code}
                    {copied ? (
                        <Check className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                        <Copy className="w-3.5 h-3.5 text-gray-400" />
                    )}
                </button>
            </td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className={`${promo.rewardType === 'free' ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-indigo-50 text-indigo-600 border-indigo-100'} px-3 py-1 rounded-lg font-medium`}
                >
                    {reward}
                </Badge>
            </td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className={`${STATUS_BADGE[promo.status]} px-3 py-1 rounded-lg font-medium capitalize`}
                >
                    {promo.status}
                </Badge>
            </td>
            <td className="px-4 py-4 text-sm whitespace-nowrap text-gray-700 tabular-nums">{usageLabel(promo)}</td>
            <td className="px-4 py-4 text-sm text-gray-600 max-w-[200px] truncate">{promo.note || '—'}</td>
            <td className="px-4 py-4 text-xs text-gray-500">
                {promo.usedByEmail ? (
                    <>
                        <p className="text-gray-800">{promo.usedByEmail}</p>
                        <p>{fmt(promo.usedAt)}</p>
                    </>
                ) : (
                    '—'
                )}
            </td>
            <td className="px-4 py-4 text-xs text-gray-500">{fmt(promo.expiresAt)}</td>
            <td className="px-4 py-4 text-right">
                <div className="inline-flex items-center justify-end gap-2">
                    {/* Edit stays available on used/disabled rows — raising the cap
                        is how an exhausted code gets brought back. */}
                    <button
                        type="button"
                        onClick={onEdit}
                        className="inline-flex items-center gap-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 px-3 py-1.5 rounded-lg text-xs font-medium"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                    </button>
                    {promo.status === 'active' && (
                        <button
                            type="button"
                            onClick={onDisable}
                            className="inline-flex items-center gap-1.5 border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-medium"
                        >
                            <Ban className="w-3.5 h-3.5" />
                            Disable
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function CreatePromoModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const [createPromo, { isLoading }] = useCreatePromoCodeMutation();
    const [rewardType, setRewardType] = useState<'percentage' | 'free'>('percentage');
    const [percentOff, setPercentOff] = useState('20');
    const [code, setCode] = useState('');
    const [note, setNote] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [limitMode, setLimitMode] = useState<'single' | 'limited' | 'unlimited'>('single');
    const [maxRedemptions, setMaxRedemptions] = useState('50');
    const [oncePerUser, setOncePerUser] = useState(true);
    const [error, setError] = useState('');

    const reset = () => {
        setRewardType('percentage');
        setPercentOff('20');
        setCode('');
        setNote('');
        setExpiresAt('');
        setLimitMode('single');
        setMaxRedemptions('50');
        setOncePerUser(true);
        setError('');
    };

    const handleSubmit = async () => {
        setError('');
        const payload: CreatePromoCodeRequest = { rewardType };
        if (rewardType === 'percentage') {
            const pct = Number(percentOff);
            if (!Number.isInteger(pct) || pct < 1 || pct > 100) {
                setError('Percent off must be a whole number between 1 and 100.');
                return;
            }
            payload.percentOff = pct;
        }
        if (code.trim()) payload.code = code.trim().toUpperCase();
        if (note.trim()) payload.note = note.trim();
        if (expiresAt) payload.expiresAt = new Date(expiresAt).toISOString();

        // 'single' deliberately omits the field rather than sending 1 — that is
        // the pre-existing default, and keeping it off the wire means old and
        // new create calls stay byte-identical.
        if (limitMode === 'unlimited') {
            payload.maxRedemptions = null;
        } else if (limitMode === 'limited') {
            const max = Number(maxRedemptions);
            if (!Number.isInteger(max) || max < 1) {
                setError('Number of users must be a whole number of 1 or more.');
                return;
            }
            payload.maxRedemptions = max;
        }
        // Only sent when turned off; true is the server default.
        if (!oncePerUser) payload.oncePerUser = false;

        try {
            await createPromo(payload).unwrap();
            reset();
            onClose();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to create promo code.'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="ml-auto h-full w-full md:max-w-md bg-white shadow-2xl z-10 flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">Create promo code</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reward type</label>
                        <div className="grid grid-cols-2 gap-2">
                            {(['percentage', 'free'] as const).map((rt) => (
                                <button
                                    key={rt}
                                    type="button"
                                    onClick={() => setRewardType(rt)}
                                    className={`px-4 py-2.5 rounded-lg text-sm font-medium border capitalize ${
                                        rewardType === rt
                                            ? 'border-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]'
                                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {rt === 'percentage' ? 'Percentage off' : 'Free'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {rewardType === 'percentage' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Percent off <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                value={percentOff}
                                onChange={(e) => setPercentOff(e.target.value.replace(/[^\d]/g, ''))}
                                placeholder="20"
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6B35]"
                            />
                            <p className="text-xs text-gray-400 mt-1">1 to 100.</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Code <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                            placeholder="Leave blank to auto generate"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg uppercase focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>

                    <fieldset>
                        <legend className="block text-sm font-medium text-gray-700 mb-2">
                            How many can redeem it?
                        </legend>
                        <div className="flex flex-col gap-2">
                            {(
                                [
                                    ['single', 'Single use'],
                                    ['limited', 'Limited number of users'],
                                    ['unlimited', 'Unlimited'],
                                ] as ['single' | 'limited' | 'unlimited', string][]
                            ).map(([value, label]) => (
                                <label key={value} className="flex items-center gap-2.5 text-sm text-gray-700">
                                    <input
                                        type="radio"
                                        name="create-limit-mode"
                                        checked={limitMode === value}
                                        onChange={() => setLimitMode(value)}
                                        className="h-4 w-4 accent-[#FF6B35]"
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>

                        {limitMode === 'limited' && (
                            <input
                                type="number"
                                min={1}
                                step={1}
                                value={maxRedemptions}
                                onChange={(e) => setMaxRedemptions(e.target.value)}
                                aria-label="Number of users who can redeem"
                                className="mt-3 w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6B35]"
                            />
                        )}

                        {limitMode !== 'single' && (
                            <label className="mt-3 flex items-start gap-2.5 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={oncePerUser}
                                    onChange={(e) => setOncePerUser(e.target.checked)}
                                    className="mt-0.5 h-4 w-4 accent-[#FF6B35]"
                                />
                                <span>
                                    One redemption per user
                                    <span className="block text-xs text-gray-500">
                                        Stops a single person draining the whole allocation.
                                    </span>
                                </span>
                            </label>
                        )}
                    </fieldset>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. Launch campaign"
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expires at <span className="text-gray-400">(optional)</span>
                        </label>
                        <input
                            type="date"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF6B35]"
                        />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outline"
                        className="flex-1 py-2.5 rounded-lg border-gray-300 hover:bg-gray-100"
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white py-2.5 rounded-lg font-medium"
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Creating...
                            </span>
                        ) : (
                            'Create code'
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
